import CouponsService from '@/api/services/coupons.service';
import { PermissionType } from '@/enums/PermissionType';
import type { ICoupon } from '@/interfaces/ICoupon';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
} from '../tool-types';
import {
  getLimitArg,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalString,
  getPageArg,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { ensurePermissionOrThrow } from '../tool-scope';
import { omitNullish, sanitizeCouponUse } from '../sanitize';

const ensureCouponsAccess = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.StoreSettings,
    message: 'No tiene permisos para ver cupones',
  });

export const couponsTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'coupons_list',
      description:
        'List the branch discount coupons with their full configuration (value, validity window, usage limits, restrictions) and current use counts. For who used one, follow up with coupons_getUses.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          status: {
            type: 'string',
            enum: ['valid', 'expired', 'all'],
            default: 'all',
          },
          search: {
            type: 'string',
            description: 'Coupon code or name.',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
        },
      },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageStore,
    async execute(args, { deps, scope }) {
      ensureCouponsAccess(deps);

      const status =
        getOptionalEnum(args, 'status', ['valid', 'expired', 'all'] as const) ??
        'all';
      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });

      const data = await runToolApiCall(() =>
        CouponsService.list(scope.restaurantId, scope.branchId, {
          status,
          page,
          itemsPerPage: limit,
          search: search ?? undefined,
        }),
      );

      return {
        total: data.total,
        page,
        validCount: data.validCount,
        expiredCount: data.expiredCount,
        results: (data.items ?? []).map((c: ICoupon) => omitNullish({ ...c })),
      };
    },
  },
  {
    definition: {
      name: 'coupons_getUses',
      description:
        'Usage history of one coupon: each redemption with its order id, discount amount and (when saved) the customer. Follow up with orders_get for order detail.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          couponId: { type: 'number' },
          search: {
            type: 'string',
            description: 'Customer name or order id.',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
        },
        required: ['couponId'],
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageStore,
    async execute(args, { deps, scope }) {
      ensureCouponsAccess(deps);

      const couponIdRaw = getOptionalNumber(args, 'couponId');
      if (couponIdRaw === null || couponIdRaw <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "couponId"', {
          couponId: args.couponId,
        });
      }
      const couponId = Math.floor(couponIdRaw);
      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });

      const data = await runToolApiCall(() =>
        CouponsService.getUsageHistory(
          scope.restaurantId,
          scope.branchId,
          couponId,
          {
            page,
            itemsPerPage: limit,
            search: search ?? undefined,
          },
        ),
      );

      return {
        total: data.total,
        page,
        results: (data.items ?? []).map(sanitizeCouponUse),
      };
    },
  },
];
